import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
import MedicationLiquidOutlined from "@mui/icons-material/MedicationLiquidOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { getChemistName } from "@/utils/textUtils";
import AdminStatCard from "@/components/admin/AdminStatCard";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import {
  ADMIN_TOP_CARD_HEIGHT,
  adminDateRangePickerWideNestedSx,
  mediDropDataGridSx,
} from "@/utils/adminTableStyles";

const TEAL             = "#083b4b";
const TEAL_DARK        = "#052830";
const TEAL_LIGHT       = "#c5e8f0";
const HEADER_GREEN      = "#94d628";
const HEADER_GREEN_TEXT = "#1a2e00";

const AdminWebbitStatCard = (props) => (
  <AdminStatCard
    gradientFrom={HEADER_GREEN}
    gradientTo="#7ab820"
    borderColor={HEADER_GREEN}
    titleColor={HEADER_GREEN_TEXT}
    valueColor={HEADER_GREEN_TEXT}
    iconGhostColor={HEADER_GREEN_TEXT}
    {...props}
  />
);

const webbitDateRangeSx = {
  borderRadius: "8px",
  py: 1.5,
  px: 1.5,
  height: ADMIN_TOP_CARD_HEIGHT,
  minHeight: ADMIN_TOP_CARD_HEIGHT,
  maxHeight: ADMIN_TOP_CARD_HEIGHT,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
  backgroundColor: "#7ab820",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  "& .text-black": { color: `${HEADER_GREEN_TEXT} !important` },
  ...adminDateRangePickerWideNestedSx,
};

function fmtDate(iso) {
  if (!iso) return "—";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
}

const AdminWebbitPage = () => {
  const navigate = useNavigate();
  const chemistBasePath = "/admin-functions/chemist";

  const [data, setData]           = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd]     = useState("");
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [tableFilter, setTableFilter] = useState("");

  // Default: last 30 days
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setDateStart(thirtyDaysAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await phappsRequest(
        API_PATHS.ADMIN_FUNCTIONS_WEBBIT,
        "GET",
        null,
        { DateStart: dateStart, DateEnd: dateEnd }
      );
      setData(res);
      setFetchError(null);
    } catch (e) {
      setData(null);
      setFetchError(getAdminApiErrorMessage(e, "Failed to load Webbit data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateStart && dateEnd) load();
  }, [dateStart, dateEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = data?.Summary || {};
  const totalPacks    = Number(summary.TotalPacks    ?? 0);
  const avgPerDay     = Number(summary.AvgPacksPerDay ?? 0);
  const totalPatients = Number(summary.TotalPatients ?? 0);
  const topChem       = summary.TopChemist;

  const chemRows = data?.ChemistTable || [];

  const gridRows = chemRows.map((row, i) => ({
    id:           row.ChemistUUID || `r-${i}`,
    chemistUUID:  row.ChemistUUID ?? "",
    chemistName:  row.ChemistName,
    packsSupplied: row.PacksSupplied ?? 0,
    patientCount:  row.PatientCount  ?? 0,
    latestSupply:  row.LatestSupply  ?? null,
  }));

  const filteredRows = useMemo(() => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return gridRows;
    return gridRows.filter((r) =>
      String(getChemistName(r.chemistName) || "").toLowerCase().includes(q)
    );
  }, [gridRows, tableFilter]);

  const columns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
          <Typography
            variant="body2"
            onClick={() =>
              p.row.chemistUUID &&
              navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, { state: { tab: 6 } })
            }
            sx={{
              cursor: p.row.chemistUUID ? "pointer" : "default",
              fontWeight: 500,
              "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" },
            }}
          >
            {getChemistName(p.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "packsSupplied",
      headerName: "Packs supplied",
      width: 120,
      type: "number",
    },
    {
      field: "patientCount",
      headerName: "Active patients",
      width: 140,
      type: "number",
    },
    {
      field: "latestSupply",
      headerName: "Latest supply",
      width: 130,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#374151" }}>
            {fmtDate(p.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Button
            variant="contained"
            size="small"
            disabled={!p.row.chemistUUID}
            onClick={() =>
              navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, { state: { tab: 6 } })
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor: HEADER_GREEN,
              color: HEADER_GREEN_TEXT,
              minWidth: 60,
              "&:hover": { backgroundColor: "#7ab820" },
            }}
          >
            Open
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Typography variant="h5" fontWeight="bold" sx={{ color: TEAL }}>
          Admin Webbit overview
        </Typography>

        {fetchError ? <Alert severity="warning">{fetchError}</Alert> : null}

        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                style={{ borderColor: TEAL }}
              />
              <p className="text-gray-600">Loading Webbit data…</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
              <Box sx={webbitDateRangeSx}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={(range) => {
                    setDateStart(range[0]?.toISOString().split("T")[0]);
                    setDateEnd(range[1]?.toISOString().split("T")[0]);
                  }}
                />
              </Box>

              <div className="min-w-0">
                <AdminWebbitStatCard
                  title="Total packs supplied"
                  value={totalPacks.toLocaleString()}
                  Icon={MedicationLiquidOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminWebbitStatCard
                  title="Avg / day"
                  value={avgPerDay.toFixed(1)}
                  Icon={TrendingUpIcon}
                />
              </div>

              <div className="min-w-0">
                <AdminWebbitStatCard
                  title="Total patients"
                  value={totalPatients.toLocaleString()}
                  Icon={GroupsOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminWebbitStatCard
                  title="Most packs supplied"
                  value={
                    topChem?.ChemistName != null
                      ? getChemistName(topChem.ChemistName)
                      : "—"
                  }
                  Icon={EmojiEventsOutlinedIcon}
                  textValue
                />
              </div>
            </section>

            {/* Per-chemist table */}
            <Card
              variant="outlined"
              sx={{ minWidth: 0, borderColor: TEAL, borderRadius: 2, overflow: "hidden" }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography fontWeight={700} sx={{ color: TEAL }}>
                    Chemists ({filteredRows.length})
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Filter by chemist name"
                    value={tableFilter}
                    onChange={(e) => setTableFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 260 }}
                  />
                </Box>
                <Divider />
                <div style={{ height: 520, width: "100%", padding: "0 8px 16px" }}>
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    pageSizeOptions={[10, 20, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 20 } },
                      sorting: { sortModel: [{ field: "packsSupplied", sort: "desc" }] },
                    }}
                    disableRowSelectionOnClick
                    sx={{
                      ...mediDropDataGridSx,
                      "& .MuiDataGrid-cell": { alignItems: "center", display: "flex" },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: `${HEADER_GREEN} !important`,
                        color: `${HEADER_GREEN_TEXT} !important`,
                        borderBottom: `2px solid ${HEADER_GREEN} !important`,
                        "& .MuiDataGrid-columnSeparator": { color: "rgba(0,0,0,0.2)" },
                        "& .MuiDataGrid-sortIcon": { color: `${HEADER_GREEN_TEXT} !important`, opacity: 1 },
                        "& .MuiDataGrid-menuIconButton": { color: `${HEADER_GREEN_TEXT} !important` },
                      },
                      "& .MuiDataGrid-columnHeadersInner": { backgroundColor: `${HEADER_GREEN} !important` },
                      "& .MuiDataGrid-columnHeader": {
                        backgroundColor: `${HEADER_GREEN} !important`,
                        color: `${HEADER_GREEN_TEXT} !important`,
                      },
                      "& .MuiDataGrid-columnHeader--sorted": { backgroundColor: `${HEADER_GREEN} !important` },
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminWebbitPage;
